import { useEffect, useState } from "react";
import { Target } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";
import { money, slug } from "@/lib/format";

function GoalCard({ name, goal, paid, remaining, mine }) {
  const real = (paid / goal) * 100;
  const pct = Math.min(100, real);
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid={`goal-card-${slug(name)}`}>
      <div className="flex items-center gap-2 font-heading text-lg font-bold text-slate-900"><Target className="h-5 w-5 shrink-0 text-[hsl(var(--primary))]" />{name}</div>
      <div className="mt-3 flex items-baseline justify-between gap-2 text-sm text-slate-600">
        <span>{mine ? "Our church paid" : "Paid so far"} <b className="font-mono text-base text-slate-900" data-testid={`goal-card-paid-${slug(name)}`}>{money(paid)}</b></span>
        <span>Goal <b className="font-mono text-slate-900">{money(goal)}</b></span>
      </div>
      <div className="mt-2 h-3 overflow-hidden rounded-full bg-slate-200"><div className="h-full rounded-full bg-emerald-600 transition-[width] duration-700" style={{ width: `${pct}%` }} /></div>
      <div className="mt-2 flex justify-between text-xs text-slate-500">
        <span data-testid={`goal-card-pct-${slug(name)}`}>{real.toFixed(0)}% of goal{real >= 100 ? " · Goal reached" : ""}</span>
        {!mine && <span data-testid={`goal-card-remaining-${slug(name)}`}>Remaining {money(remaining)}</span>}
      </div>
    </div>
  );
}

export function GoalCards({ enabled }) {
  const ctx = useApp();
  const goals = ctx.options.collections.filter((c) => c.active && c.goal > 0).map((c) => ({ ...c, total: ctx.churches.reduce((a, ch) => a + Number(c.church_goals?.[ch.id] ?? c.goal), 0) })).filter((c) => c.total > 0);
  const key = goals.map((g) => g.name).join("|");
  const [paid, setPaid] = useState({});
  useEffect(() => {
    if (!enabled || !goals.length) return undefined;
    let live = true;
    Promise.all(goals.map((g) => api.get("/donations", { params: { collection: g.name } }).then((r) => [g.name, r.data]).catch(() => [g.name, []])))
      .then((list) => { if (live) { setPaid(Object.fromEntries(list)); } });
    return () => { live = false; };
  }, [enabled, key]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!enabled || !goals.length) return null;
  return (
    <section data-testid="goal-cards">
      <h2 className="text-on-bg mb-3 font-heading text-xl font-bold">Conference goals</h2>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        {goals.map((g) => {
          const list = paid[g.name] || [];
          const of = (ch) => list.filter((d) => d.church_id === ch.id).reduce((a, d) => a + Number(d.amount || 0), 0);
          const remaining = ctx.churches.reduce((a, ch) => a + Math.max(Number(g.church_goals?.[ch.id] ?? g.goal) - of(ch), 0), 0);
          return <GoalCard key={g.id} name={g.name} goal={g.total} paid={list.reduce((a, d) => a + Number(d.amount || 0), 0)} remaining={remaining} mine={!ctx.isAdmin} />;
        })}
      </div>
    </section>
  );
}
